import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { format, isValid, parseISO } from 'date-fns';

const PersonnelApplicationCard = ({
   application,
   actions,
   getStatusColor,
   className,
   showActionLabels = false
}) => {
   const statusColor = getStatusColor(application.status);

   const formatDate = (dateString) => {
      try {
         const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
         return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Date not available';
      } catch (error) {
         console.error('Date formatting error:', error);
         return 'Date not available';
      }
   };

   // Render action buttons with tooltips when labels are hidden
   const renderActionButtons = () => {
      return actions.map((action, index) => (
         <TooltipProvider key={index} delayDuration={200}>
            <Tooltip>
               <TooltipTrigger asChild>
                  <Button
                     variant={action.variant || "outline"}
                     size={showActionLabels ? "sm" : "icon"}
                     className={cn(
                        "h-9",
                        action.className,
                        showActionLabels
                           ? "text-xs flex items-center gap-2 px-3 min-w-[90px]"
                           : "w-9 p-0 flex items-center justify-center"
                     )}
                     onClick={action.onClick}
                  >
                     {action.icon && <action.icon className="h-4 w-4 shrink-0" />}
                     {showActionLabels && (
                        <span className="truncate max-w-[100px]">{action.label}</span>
                     )}
                  </Button>
               </TooltipTrigger>
               {!showActionLabels && (
                  <TooltipContent>
                     <p>{action.label} {action.hotkey ? `(${action.hotkey})` : ''}</p>
                  </TooltipContent>
               )}
            </Tooltip>
         </TooltipProvider>
      ));
   };

   return (
      <div className={cn(
         "bg-card rounded-lg border p-6 shadow-sm transition-all hover:shadow-md",
         "flex flex-col",
         className
      )}>
         {/* Header with Application Number and Assigned To */}
         <div className="mb-2 space-y-1">
            {/* Header with Application Number and Status side by side */}
            <div className="flex items-center justify-between">
               <h3 className="font-semibold text-lg">{application.applicationNumber}</h3>
               <Badge className={cn("w-fit", statusColor.className)}>
                  {application.status}
               </Badge>
            </div>

            <p className="text-sm font-bold">
               {'Assigned to: '} <span className="text-sm font-normal">{application.assignedTechnicalStaffName}</span>
            </p>

            <div className="space-y-1">
               <p className="text-sm font-bold" >
                  {'Application Type: '} <span className="text-sm font-normal">{application.applicationType}</span>
               </p>
            </div>

            {/* Request Type */}
            <div className="space-y-1 mb-1">
               <p className="text-sm font-bold" >
                  {'Request Type: '} <span className="text-sm font-normal">{application.requestType || 'N/A'}</span>
               </p>
            </div>

            {/* Date */}
            <div className="space-y-1">
               {/* <p className="text-sm font-medium text-muted-foreground">Date Submitted</p> */}
               <p className="text-sm">{formatDate(application.dateOfSubmission)}</p>
            </div>
         </div>

         {/* Actions Section */}
         <div className={cn(
            "pt-4 border-t flex flex-wrap gap-2",
            showActionLabels ? "justify-start" : "justify-end"
         )}>
            {renderActionButtons()}
         </div>
      </div>
   );
};

export default PersonnelApplicationCard;